<template>
  <listing-layout>
    <h1 slot="heading" class="h2">Select Member To Merge</h1>
    <div slot="content">
      <div class="alert alert-success" v-if="success">{{success}}</div>
      <div class="alert alert-warning" v-else-if="errors">{{errors}}</div>
      <h2 class="h4">This member will be kept</h2>
      <div class="bg-lighter rounded mb-3 py-2 px-3">
        <div class="row">
          <div class="col-md-auto">
            <span v-show="memberDetails.member_number">{{ memberDetails.member_number }}: </span>{{memberDetails.first_name }} {{memberDetails.last_name }} <br>
          </div>
          <div class="col-md-auto" v-if="memberZone">
            {{ 'Zone: ' + memberZone }}
          </div>
          <div class="col-md-auto" v-if="memberDetails.dob">
            {{ 'DoB: ' + new Date(memberDetails.dob.replace(/-/g, '/')).toLocaleDateString('en-AU') }} <br>
          </div>
          <div class="col-md-auto" v-if="memberDetails.membership_status">
            {{ 'Status: ' + memberDetails.membership_status }}
          </div>
        </div>
      </div>
      <h2 class="h4">Select member to merge with the above</h2>
      <p>Only one member can be selected from the table below for merge with the above member.</p>

      <div class="form-inline mb-sm-3 d-flex justify-content-end">
        <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 mt-xl-0 ml-auto px-0 action_section">
          <label for="mergeMembersSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
          <div class="input-append d-flex flex-grow-1 flex-md-grow-0 mr-md-2">
            <input
              id="mergeMembersSearch"
              v-model="searchInput"
              type="text"
              class="form-control mb-0 flex-grow-1 flex-md-grow-0"
              @keyup.enter="applySearch"
              @search="applySearch"
            >
            <svg
              class="append"
              role="button"
              aria-label="Search"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              width="16"
              height="16"
              @click="applySearch"
            >
              <rect width="100%" height="100%" fill="none" />
              <g>
                <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
                <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
              </g>
            </svg>
          </div>
        </div>
      </div>

      <div class="position-relative" style="min-height: 240px">
        <responsive-table
          :columns="columns"
          :rows="visibleRows"
          :loading="loading"
          :breakpoints="breakpoints"
          :sort="filters.sort"
          :direction="filters.direction"
          empty-text="No members found."
          @sort="setSort"
        >
          <template #cell:select="{ row }">
            <div class="custom-control custom-checkbox custom-control-inline mr-0">
              <input
                type="checkbox"
                :id="`mergeSelect${row.id}`"
                class="custom-control-input"
                :checked="isSelected(row.id)"
                @change="toggleSelect(row)"
              >
              <label class="custom-control-label" :for="`mergeSelect${row.id}`"></label>
            </div>
          </template>

          <template #cell:name="{ row }">
            <list-name-cell
              :label="`${row.first_name} ${row.last_name}`"
              :to="{ name: 'Member Profile', params: { slug: row.id } }"
            />
          </template>

          <template #cell:first_name="{ row }">
            <router-link :to="{ name: 'Member Profile', params: { slug: row.id } }">{{ row.first_name }}</router-link>
          </template>
          <template #cell:last_name="{ row }">
            <router-link :to="{ name: 'Member Profile', params: { slug: row.id } }">{{ row.last_name }}</router-link>
          </template>
          <template #cell:member_number="{ row }">
            <list-name-cell align="right" :label="row.member_number">
              <template #badges>
                <span v-if="row.is_primary" class="badge alert-prim">Primary</span>
              </template>
            </list-name-cell>
          </template>
          <template #cell:zone_id="{ row }">{{ zoneCode(row.zone_id) }}</template>
        </responsive-table>
      </div>

      <div class="form-inline justify-content-between align-items-start mt-3">
        <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
          <label for="mergeMembersShow" class="mr-2 mb-0 font-weight-normal">Show</label>
          <select
            id="mergeMembersShow"
            class="form-control mr-2"
            :value="filters.per_page"
            @change="setPerPage(Number($event.target.value))"
          >
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
          <span>Entries</span>
        </div>

        <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
          <pagination
            :total-pages="totalPages"
            :page="page"
            :on-change="setPage"
            :disabled="loading"
          />
        </div>
      </div>

      <div class="sticky-bottom alert bg-primary text-white d-flex flex-wrap align-items-center mb-0" role="alert" v-if="Object.keys(memberDetails).length && Object.keys(removeMember).length">
        <div class="flex-grow-1 pr-3 text-center text-lg-left">
          <strong>You have selected: </strong>
          {{ removeMember.first_name + ' ' + removeMember.last_name }}
        </div>
        <button class="btn btn-outline-white btn-block-mob mt-2 mt-lg-0 text-nowrap" @click="showModal = true">Merge Selected</button>
        <button class="btn btn-outline-white ml-lg-2 btn-block-mob" @click.prevent="deselectAll">Cancel</button>
      </div>

      <!-- use the modal component, pass in the prop -->
      <modal v-if="showModal" @close="showModal = false">
        <h3 slot="header" class="modal-title">Please confirm the members to be merged</h3>
        <div slot="body">
          <div class="alert alert-warning">
            This operation cannot be undone.
          </div>
          <div class="row">
            <div class="col-md-6">
              <h4>Member To Keep</h4>
              <div class="bg-lighter rounded mb-3 py-2 px-3">
                <div class="row">
                  <div class="col-md-auto">
                    {{ memberDetails.member_number + ': ' + memberDetails.first_name + ' ' + memberDetails.last_name }} <br>
                  </div>
                  <div class="col-md-auto" v-if="memberZone">
                    {{ 'Zone: ' + memberZone }}
                  </div>
                  <div class="col-md-auto" v-if="memberDetails.dob">
                    {{ 'DoB: ' + new Date(memberDetails.dob.replace(/-/g, '/')).toLocaleDateString('en-AU') }} <br>
                  </div>
                  <div class="col-md-auto" v-if="memberDetails.membership_status">
                    {{ 'Status: ' + memberDetails.membership_status }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <h4>Member To Remove</h4>
              <div class="bg-lighter rounded mb-3 py-2 px-3">
                <div class="row">
                  <div class="col-md-auto">
                    {{ removeMember.member_number + ': ' + removeMember.first_name + ' ' + removeMember.last_name }} <br>
                  </div>
                  <div class="col-md-auto" v-if="removeMemberZone">
                    {{ 'Zone: ' + removeMemberZone }}
                  </div>
                  <div class="col-md-auto" v-if="removeMember.dob">
                    {{ 'DoB: ' + new Date(removeMember.dob.replace(/-/g, '/')).toLocaleDateString('en-AU') }} <br>
                  </div>
                  <div class="col-md-auto" v-if="removeMember.membership_status">
                    {{ 'Status: ' + removeMember.membership_status }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div slot="footer" class="w-100 d-flex justify-content-end">
          <button class="btn btn-primary" @click.prevent="mergeMembers(); showModal = false">Confirm</button>
          <button class="btn btn-outline-primary ml-2" @click.prevent="deselectAll(); showModal = false">Cancel</button>
        </div>
      </modal>
    </div>
  </listing-layout>
</template>

<script>
/* Use the "slot" attribute to match the heading and content
 * to their respective fields in the ListingLayout component. */

// TODO feature/OSESD-837: Add merge button in confirmation modal connecting to BE logic - check BE connection
import axios from 'axios'
import { ref, reactive, watch, onMounted, onBeforeUnmount, getCurrentInstance } from '@vue/composition-api'
import GETSTORE from '@/store/memberProfileStore.js'
import listingLayout from '@/components/ListingLayout'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import ListNameCell from '@/components/ListNameCell'
import modal from '@/components/Modal'
import membersApi from '@/services/api/members'
import { buildColumns, buildBreakpoints } from './columns'

const PER_PAGE_OPTIONS = [20, 50, 100, 200]

export default {
  name: 'MemberMerge',
  mixins: [GETSTORE],
  components: {
    'listing-layout': listingLayout,
    ResponsiveTable,
    Pagination,
    ListNameCell,
    modal
  },
  // Mirrors views/Members/Join/MemberJoin.vue's server-paginated picker table (same
  // ResponsiveTable + Pagination + checkbox shape) instead of the old unbounded GET /users dump.
  // Unlike MemberJoin, this route has a real path segment (`/members/:slug/merge`), so there's no
  // risk of `router.replace({query})` wiping route.params — but state still isn't URL-synced here,
  // matching the legacy page (its client-side vuejs-datatable pagination was never URL-persisted
  // either).
  setup() {
    const { proxy } = getCurrentInstance()
    const store = proxy.$store

    const rows = ref([])
    const loading = ref(false)
    const page = ref(1)
    const totalPages = ref(1)
    const perPageOptions = PER_PAGE_OPTIONS
    const filters = reactive({ sort: 'last_name', direction: 'asc', per_page: 20, search: '' })

    function buildParams() {
      // The legacy GET /users picker only ever returned primary accounts (secondaries have no
      // row of their own in user_attributes, the table that query was driven from) — keep that
      // scope so merge targets match what the page showed before.
      const params = { page: page.value, per_page: filters.per_page, primary_only: 1 }
      if (filters.sort) {
        params.sort = filters.sort
        params.direction = filters.direction
      }
      if (filters.search) params.search = filters.search
      return params
    }

    async function fetch() {
      loading.value = true
      try {
        const res = await membersApi.getList(buildParams())
        const body = res.data != null ? res.data : {}
        rows.value = body.data != null ? body.data : []
        const meta = body.meta != null ? body.meta : {}
        page.value = meta.current_page != null ? meta.current_page : 1
        totalPages.value = meta.last_page != null ? meta.last_page : 1
      } catch (_) {
        rows.value = []
      } finally {
        loading.value = false
      }
    }

    function setPage(p) {
      page.value = p
      fetch()
    }
    function setSort(field) {
      if (filters.sort !== field) {
        filters.sort = field
        filters.direction = 'asc'
      } else if (filters.direction === 'asc') {
        filters.direction = 'desc'
      } else {
        filters.sort = 'last_name'
        filters.direction = 'asc'
      }
      page.value = 1
      fetch()
    }
    function setPerPage(n) {
      filters.per_page = Number(n)
      page.value = 1
      fetch()
    }
    function setSearch(value) {
      filters.search = value || ''
      page.value = 1
      fetch()
    }

    const columns = buildColumns()
    const breakpoints = buildBreakpoints()

    function zoneCode(id) {
      const zone = (store.state.listZones || []).find(z => z.id === id)
      return zone ? zone.code : ''
    }

    const searchInput = ref(filters.search || '')
    let searchTimer = null
    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    onBeforeUnmount(() => clearTimeout(searchTimer))
    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })

    onMounted(fetch)

    return {
      columns,
      breakpoints,
      rows,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      applySearch,
      setPage,
      setSort,
      setPerPage,
      zoneCode,
      fetch
    }
  },
  data() {
    return {
      success: '',
      errors: '',
      removeMember: {},
      showModal: false
    }
  },
  computed: {
    memberZone() {
      const tempKeepMember = this.$store.state.listZones.find(zone => zone.id === (this.memberDetails.zone_id || (this.memberDetails.attributes && this.memberDetails.attributes.zone_id)))
      return this.$store.state.listZones.length ? tempKeepMember ? tempKeepMember.code : '' : ''
    },
    removeMemberZone() {
      return Object.keys(this.removeMember).length ? this.zoneCode(this.removeMember.zone_id) : ''
    },
    // The member being kept must never also appear as a selectable merge target.
    visibleRows() {
      return this.rows.filter(row => row.id !== this.memberDetails.id)
    }
  },
  watch: {
    // When changing user, get new user details from back-end
    '$route' (to, from) { // eslint-disable-line
      if (to.params && to.params.slug) {
        this.getMemberFromAPI(to.params.slug)
      }
    }
  },
  methods: {
    isSelected(id) {
      return this.removeMember.id === id
    },
    // Checkbox visuals, but only one member can ever be the merge target — checking a new row
    // replaces the previous selection (same "last one wins" behaviour the legacy DataTable
    // enforced via its selectedRows watcher).
    toggleSelect(row) {
      this.removeMember = this.isSelected(row.id) ? {} : row
    },
    deselectAll() {
      this.removeMember = {}
    },
    mergeMembers() {
      this.success = ''
      this.errors = ''
      axios.post(`${this.$api}/admin/merge-members/${this.memberDetails.id}/${this.removeMember.id}`).then((response) => {
        this.deselectAll()
        if (response.data.status === 'success') {
          this.success = 'Members were merged successfully.'
          this.fetch()
        } else {
          this.errors = 'There was an error. Please contact support.'
        }
      }).catch((error) => {
        // eslint-disable-next-line
        console.log(error.response)
        this.errors = error.response.data.error || 'There was an error. Please contact support.'
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Members/Merge/MemberMerge.vue